import React, { useState, useRef, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import { fromEvent as ObservableFromEvent } from 'rxjs'
import { mergeMap as rxMergeMap, map as rxMap, takeUntil as rxTakeUntil } from 'rxjs/operators'

import { DOMElementRect } from '@aytm/lib/common/func'

import colorHelper from 'react-color/lib/helpers/color'
import { calcGradientColors } from '@aytm/app/stats/lib/render/colors'
import { COLOR_ALGORITHMS } from '@aytm/app/stats/page/question-views/components/word-cloud/constants/sync'

// components
import Portal from '@aytm/lib/rfd-ui/components/basic/portal'

import { InputText } from 'primereact/inputtext'
import { ColorPicker } from 'primereact/colorpicker'

import { pressable } from '@aytm/app/stats/components/lib/pressable'

// styles
import style from '@aytm/app/stats/page/question-views/components/word-cloud/styles//ColorPickerPopup.module.scss'

const COLORS_MODES = ['adj_levels', 'levels', 'gradient', 'circle']
// a mode swatch shows no text, so it is announced by the name the colors dropdown gives the same mode
const COLORS_MODE_NAMES = Object.fromEntries(COLOR_ALGORITHMS.map(([key, name]) => [key, name]))

const ColorPickerPopup = React.forwardRef(
  ({ container, target, mode, colors, background, offsetTop, offsetLeft, onChange, onClose }, colorPickerPopupRef) => {
    const [currentColorIndex, setCurrentColorIndex] = useState(0)

    const colorPickerPopupSubscriptionRef = useRef(null)

    const currentColor = useMemo(
      () => (0 > currentColorIndex ? background : colors[currentColorIndex]),
      [currentColorIndex, colors, background]
    )

    const dragModal = () => {
      if (!colorPickerPopupRef.current) return

      const popupHeader = colorPickerPopupRef.current.querySelector(`.${style.header}`)
      if (!popupHeader) return

      const mouseup = ObservableFromEvent(document, 'mouseup')
      const mousemove = ObservableFromEvent(document, 'mousemove')
      const mousedown = ObservableFromEvent(popupHeader, 'mousedown')

      const mousedrag = mousedown.pipe(
        rxMergeMap(md => {
          // calculate start positions of modal window and cursor
          const popupRect = DOMElementRect(colorPickerPopupRef.current)

          const startY = popupRect.top - md.pageY
          const startX = popupRect.left - md.pageX

          // calculate delta with mousemove until mouseup
          return mousemove.pipe(
            rxMap(mm => {
              mm.preventDefault()
              return { top: mm.pageY + startY, left: mm.pageX + startX }
            }),
            rxTakeUntil(mouseup)
          )
        })
      )

      // update position
      colorPickerPopupSubscriptionRef.current = mousedrag.subscribe(pos => {
        if (!colorPickerPopupRef.current) return

        colorPickerPopupRef.current.style.top = `${pos.top}px`
        colorPickerPopupRef.current.style.left = `${pos.left}px`
      })

      if (target) {
        const targetRect = DOMElementRect(target)
        const popupRect = DOMElementRect(colorPickerPopupRef.current)

        let top = targetRect.top + (Number.isFinite(offsetTop) ? offsetTop : targetRect.height)
        let left = targetRect.left + (Number.isFinite(offsetLeft) ? offsetLeft : targetRect.width)

        if (container) {
          const containerRect = DOMElementRect(container)
          top = Math.min(containerRect.height - popupRect.height, Math.max(0, top))
          left = Math.min(containerRect.width - popupRect.width, Math.max(0, left))
        }

        colorPickerPopupRef.current.style.top = `${top}px`
        colorPickerPopupRef.current.style.left = `${left}px`
      }
    }

    const onColorsModeChange = colorsMode => {
      const newColors =
        'gradient' == colorsMode
          ? [colors.at(0), colors.at(-1)]
          : 2 == colors.length
            ? calcGradientColors(colors[0], colors[1], 7)
            : colors

      onChange({ mode: colorsMode, colors: newColors, background })
    }
    const onColorChange = event => {
      if (0 > currentColorIndex) {
        onChange({ mode, colors, background: `#${event.value}` })
      } else {
        const newColors = colors.slice()
        newColors[currentColorIndex] = `#${event.value}`

        onChange({ mode, colors: newColors, background })
      }
    }

    const onInputChange = event => {
      const value = event.target.value.startsWith('#') ? event.target.value : `#${event.target.value}`
      if (!colorHelper.isValidHex(value)) return

      if (0 > currentColorIndex) {
        onChange({ mode, colors, background: value })
      } else {
        const newColors = colors.slice()
        newColors[currentColorIndex] = value

        onChange({ mode, colors: newColors, background })
      }
    }

    useEffect(() => {
      const timerId = setTimeout(dragModal, 0)

      return () => {
        clearTimeout(timerId)
        colorPickerPopupSubscriptionRef.current?.unsubscribe()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
      <Portal>
        <div ref={colorPickerPopupRef} className={classNames('PRAYTMTheme', style.component)}>
          <div className={style.header}>
            <span>mode:</span>
            <span className={style['header-modes']}>
              {COLORS_MODES.map(colorsMode => (
                <span
                  key={`mode-${colorsMode}`}
                  className={classNames(style['header-mode'], style[`header-mode-${colorsMode}`], {
                    [style['header-mode-active']]: colorsMode == mode
                  })}
                  aria-label={COLORS_MODE_NAMES[colorsMode]}
                  {...pressable(() => onColorsModeChange(colorsMode))}
                ></span>
              ))}
            </span>
            <div className={style['header-close-button']} aria-label="Close" {...pressable(onClose)}>
              <i className="icon-aytmfd-x-v2" />
            </div>
            <span className={style['header-handle']}>
              <i className="icon-aytmfd-handle" />
            </span>
          </div>
          <div className={style.content}>
            <div className={classNames(style.palette, style[`palette-${mode}`])}>
              {'gradient' == mode ? (
                <>
                  <span
                    className={classNames(style['palette-color'], {
                      [style['palette-color-active']]: 0 == currentColorIndex
                    })}
                    aria-label="Color 1"
                    {...pressable(() => setCurrentColorIndex(0))}
                    style={{ backgroundColor: colors[0] }}
                  ></span>
                  <span
                    className={style['palette-color-gradient']}
                    style={{ background: `linear-gradient(to right, ${colors[0]} 0%, ${colors[1]} 100%)` }}
                  ></span>
                  <span
                    className={classNames(style['palette-color'], {
                      [style['palette-color-active']]: 1 == currentColorIndex
                    })}
                    aria-label="Color 2"
                    {...pressable(() => setCurrentColorIndex(1))}
                    style={{ backgroundColor: colors[1] }}
                  ></span>
                </>
              ) : (
                colors.map((color, index) => (
                  <span
                    key={`color-${index + 1}`}
                    className={classNames(style['palette-color'], {
                      [style['palette-color-active']]: index == currentColorIndex
                    })}
                    aria-label={`Color ${index + 1}`}
                    {...pressable(() => setCurrentColorIndex(index))}
                    style={{ backgroundColor: color }}
                  ></span>
                ))
              )}
              <div className={style['palette-background']}>
                {/* <span className={style['palette-background-label']}>Background:</span> */}
                <span
                  className={classNames(style['palette-color'], {
                    [style['palette-color-active']]: -1 == currentColorIndex
                  })}
                  aria-label="Background color"
                  {...pressable(() => setCurrentColorIndex(-1))}
                  style={{ backgroundColor: background }}
                ></span>
              </div>
            </div>
            <div className={style.row}>
              <div className={`${style.selector} p-fluid p-col-12`}>
                <InputText
                  className="p-inputtext-rounded"
                  aria-label="Hex color"
                  value={currentColor}
                  onChange={onInputChange}
                />
                <span className={style['selector-color']} style={{ backgroundColor: currentColor }}></span>
              </div>
            </div>
            <div className={style.row}>
              <div className={`${style.colorpicker} p-col-12`}>
                <ColorPicker value={currentColor} inline onChange={onColorChange} />
              </div>
            </div>
          </div>
        </div>
      </Portal>
    )
  }
)

ColorPickerPopup.propTypes = {
  container: PropTypes.any,
  target: PropTypes.any,
  mode: PropTypes.string.isRequired,
  colors: PropTypes.array.isRequired,
  background: PropTypes.string.isRequired,
  offsetTop: PropTypes.number,
  offsetLeft: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  onClose: PropTypes.func
}

ColorPickerPopup.displayName = 'ColorPickerPopup'

export default ColorPickerPopup
